import { useState, useEffect } from "react";
import { Phone, Mail, Menu, X, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSite } from "@/context/SiteContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_6d3eae7a-9e3d-44ef-92c9-21137f2ed44d/artifacts/dh0tvqgx_Original%20Logo%20Symbol.png";

const navLinks = [
  { id: "nav-accueil", label: "Accueil", href: "#accueil" },
  { id: "nav-services", label: "Services", href: "#services" },
  { id: "nav-pourquoi", label: "Pourquoi nous", href: "#pourquoi" },
  { id: "nav-galerie", label: "Réalisations", href: "#galerie" },
  { id: "nav-faq", label: "FAQ", href: "#faq" },
  { id: "nav-contact", label: "Contact", href: "#contact" },
];

export const Header = () => {
  const { settings } = useSite();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const nav = (href) => {
    setMobileOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const phone = settings?.phone || "514-775-5027";
  const email = settings?.email || "lesentretienselites@gmail.com";

  return (
    <>
      {/* Banner */}
      {settings?.banner_active && settings?.banner_text && (
        <div className="bg-[#2D5A3D] text-white text-center py-2 px-4 text-sm font-medium" data-testid="site-banner">
          {settings.banner_text}
        </div>
      )}

      {/* Top Bar */}
      <div className="bg-[#1E3A28] text-white py-2 px-4" data-testid="top-bar">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href={`tel:${phone.replace(/-/g, "")}`} className="flex items-center gap-1.5 text-white hover:text-green-200 transition-colors" data-testid="top-bar-phone">
              <Phone className="w-3.5 h-3.5" /><span className="font-medium">{phone}</span>
            </a>
            <a href={`mailto:${email}`} className="hidden sm:flex items-center gap-1.5 text-green-200/80 hover:text-white transition-colors" data-testid="top-bar-email">
              <Mail className="w-3.5 h-3.5" /><span>{email}</span>
            </a>
          </div>
          <span className="hidden md:block text-green-200/70 text-xs">Soumission 100% gratuite</span>
        </div>
      </div>

      {/* Nav */}
      <nav className={`sticky top-0 z-50 bg-white border-b transition-shadow ${scrolled ? "shadow-md" : ""}`} data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="#accueil" onClick={(e) => { e.preventDefault(); nav("#accueil"); }} className="flex items-center gap-2.5" data-testid="logo-link">
              <img src={LOGO_URL} alt="Les Entretiens Élites" className="h-10 w-10 rounded" />
              <div className="leading-tight">
                <span className="text-[#2D5A3D] font-bold text-base block">Les Entretiens</span>
                <span className="text-[#2D5A3D] font-bold text-base -mt-0.5 block">Élites</span>
              </div>
            </a>

            <div className="hidden lg:flex items-center gap-0.5">
              {navLinks.map((l) => (
                <button key={l.id} onClick={() => nav(l.href)} className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-[#2D5A3D] rounded-md hover:bg-green-50 transition-colors" data-testid={l.id}>
                  {l.label}
                </button>
              ))}
              <Button onClick={() => nav("#contact")} className="ml-3 bg-[#2D5A3D] hover:bg-[#234A30] text-white" data-testid="nav-cta-button">
                Soumission gratuite <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            <div className="flex items-center gap-2 lg:hidden">
              <a href={`tel:${phone.replace(/-/g, "")}`} className="p-2 text-[#2D5A3D]" data-testid="mobile-phone-btn"><Phone className="w-5 h-5" /></a>
              <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-gray-700" data-testid="mobile-menu-toggle">
                {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t bg-white" data-testid="mobile-menu">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((l) => (
                <button key={l.id} onClick={() => nav(l.href)} className="block w-full text-left px-3 py-2.5 text-base font-medium text-gray-700 hover:text-[#2D5A3D] hover:bg-green-50 rounded-md" data-testid={`mobile-${l.id}`}>
                  {l.label}
                </button>
              ))}
              <Button onClick={() => nav("#contact")} className="w-full mt-2 bg-[#2D5A3D] hover:bg-[#234A30] text-white" data-testid="mobile-cta-button">
                Soumission gratuite
              </Button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};
